/**
 * アンカーの時刻を決める（索引 Issue #498 の段階 2。設計文書 §4・§5）。**純関数**。
 *
 * **アンカーが時刻を持ち、従属オブジェクトはそれに従う**（`start = anchor.ms`）。
 * どう決めるかは**戦略で差し替える**:
 *
 *   fixed       ソースの時刻をそのまま使う（既定）
 *   sequential  そのアンカーに付いた帯が前の帯と重ならないよう、アンカーごと後ろへ送る
 *
 * `sequential` は旧演出プレビュー（Issue #639 で削除）の整列を**ドメイン抜き**にしたもの。
 * 「応答（op）」はここでは `anchor.groupId`、「イベントかどうか」は `anchor.ordering` になる。
 * 再現エンジンのソース（`editorPorts/ocgSource.js`）は `SourcePort.alignStrategy()` で `sequential` を選ぶ。
 *
 * 人が触ったアンカー（文書の `anchors[key].ms`）は動かさない。並びの逆転は編集操作の側で拒むが、
 * 古い文書やソースの打ち直しで逆転したまま入ってくることはあるので、そのときだけ直前まで詰めて `clamped` で知らせる。
 */
import { DEFAULT_FPS, snapToFrame } from './time';
import { isStateLayer } from './types';

const isFin = Number.isFinite;

/** 整列に関わる帯（隠したもの・常時表示は並べない） */
const isLive = (o) => !o.hidden && !isStateLayer(o.layer);
/** 整列が「直前の帯と重ねてよいか」を引く名前（帯の `overlapKey`、無ければ型の id。Issue #556） */
export const overlapKeyOf = (o) => o?.overlapKey ?? o?.type ?? null;
const durationOf = (o) => (isFin(o.durationMs) ? o.durationMs : (o.minDurationMs ?? 1000));

/** アンカー列と文書の上書きから、整列前の行を作る */
function baseRows(anchors, overrides) {
  return anchors.map((a, i) => {
    const ov = a.key ? overrides[a.key] : null;
    return {
      ...a,
      key: a.key ?? null,
      index: isFin(a.index) ? a.index : i,
      ordering: a.ordering !== false,
      baseMs: isFin(a.ms) ? a.ms : null,
      touched: isFin(ov?.ms),
      hidden: ov?.hidden === true,
      overrideMs: isFin(ov?.ms) ? ov.ms : null,
    };
  });
}

/** 帯を持つかどうかを行に足す（整列のあと、後ろ向きのパスと `anchorMoveRange` が見る） */
const withHasObjects = (row, count) => ({ ...row, hasObjects: count > 0 });

// ---------------------------------------------------------------- 戦略
/** ソースの時刻をそのまま（人が触ったところだけ上書き）。単調にだけ整える */
function fixedStrategy({ rows, bySource, fps }) {
  const out = [];
  let last = -Infinity;
  for (const row of rows) {
    const mine = row.hidden ? [] : (bySource.get(row.key) ?? []);
    let ms = row.touched ? row.overrideMs : (row.baseMs ?? last);
    let clamped = false;
    if (row.ordering && isFin(last) && ms < last) { ms = last; clamped = row.touched; }
    ms = Math.max(0, snapToFrame(isFin(ms) ? ms : 0, fps));
    if (row.ordering) last = ms;
    out.push(withHasObjects({ ...row, ms, clamped }, mine.length));
  }
  return { rows: out, placements: new Map() };
}

/**
 * 帯が重ならないよう順に並べる。
 * - 同じアンカーの帯は頭のずれの順に置き、重なったら後ろへ送る（**打ち切らない**）
 * - アンカーをまたぐ重なりも同じ cursor で解消する
 * - 重ねてよい相手（`overlaps: { <重ねの鍵>: 間 ms }`）は、直前に置いた帯の鍵がそれなら頭 + 間で重ねる。
 *   **重ねの鍵は帯の `overlapKey`、無ければ型の id**（Issue #556。型単位だけでなく帯ごとに相手を決められる）
 * - 帯の無いアンカーは、それまでの表示が終わった時刻に置く
 * - 人が触ったアンカーは動かさず、その帯は前の帯を避けない
 * - `groupId` が同じアンカーのどれかを人が触ったら、以後その group では `baseMs` を下限にしない
 *   （先頭を 1 つ動かせば一連の流れが付いてくる）
 */
function sequentialStrategy({ rows, bySource, fps }) {
  const state = { maxEndByKey: new Map(), last: null };
  const put = (s, box) => {
    s.maxEndByKey.set(box.key, Math.max(s.maxEndByKey.get(box.key) ?? -Infinity, box.endMs));
    s.last = box;
  };
  /** その帯を置ける一番早い時刻 */
  const targetOf = (o, s) => {
    const overlaps = o.overlaps ?? {};
    const stacking = Boolean(s.last && overlaps[s.last.key] != null);
    let end = -Infinity;
    s.maxEndByKey.forEach((v, key) => { if (!(stacking && overlaps[key] != null)) end = Math.max(end, v); });
    return stacking ? Math.max(end, s.last.startMs + overlaps[s.last.key]) : end;
  };

  const placements = new Map();
  const endsByIndex = new Map();
  const anchoredGroups = new Set();
  const out = [];
  let cursor = -Infinity;
  let lastMs = -Infinity;
  let lastFirmMs = -Infinity;

  for (const row of rows) {
    const own = row.key ? (bySource.get(row.key) ?? []) : [];
    const mine = row.hidden ? [] : own.filter(isLive);
    const floor = Math.max(lastMs, isFin(row.baseMs) && !anchoredGroups.has(row.groupId ?? null) ? row.baseMs : -Infinity);

    let ms;
    let clamped = false;
    if (row.touched) { ms = Math.max(row.overrideMs, lastFirmMs); clamped = ms !== row.overrideMs; } else if (!mine.length) {
      ms = Math.max(floor, cursor);
    } else {
      // アンカーの時刻は「最初の帯」が置ける時刻で決まる
      const t = targetOf(mine[0], state);
      ms = isFin(t) ? Math.max(floor, t) : floor;
    }
    if (!isFin(ms)) ms = isFin(cursor) ? cursor : 0;
    ms = Math.max(0, snapToFrame(ms, fps));
    if (row.touched && row.groupId != null && row.ordering) anchoredGroups.add(row.groupId);

    // 帯を置く。人が触ったアンカーの帯は前の帯を避けない（そこに出したくて置いたので頭を守る）
    const scope = row.touched ? { maxEndByKey: new Map(), last: null } : state;
    let maxEnd = -Infinity;
    for (const o of mine) {
      const base = ms;
      const t = targetOf(o, scope);
      const s = isFin(t) ? Math.max(base, t) : base;
      const e = s + durationOf(o);
      placements.set(o.id, { startMs: s, endMs: e, sequenced: s !== base });
      const box = { key: overlapKeyOf(o), startMs: s, endMs: e };
      put(scope, box);
      if (scope !== state) put(state, box);
      maxEnd = Math.max(maxEnd, e);
    }
    if (isFin(maxEnd)) endsByIndex.set(row.index, maxEnd);

    out.push(withHasObjects({ ...row, ms, clamped }, mine.length));
    if (row.ordering) {
      lastMs = ms;
      if (row.touched || mine.length) lastFirmMs = ms;
      cursor = Math.max(cursor, ms, isFin(maxEnd) ? maxEnd : -Infinity);
    }
  }

  // 後ろ向きの 1 パス: 帯が無く人も触っていないアンカー（「それまでの表示が終わってから」置かれたもの）は、
  // 後ろのアンカーを人が手前へ置いたらそこまで引き戻す（並びは崩れない＝単調のまま）
  for (let i = out.length - 2; i >= 0; i -= 1) {
    const r = out[i];
    const next = out[i + 1];
    if (!r.ordering || !next.ordering) continue;
    if (!r.touched && !r.hasObjects && r.ms > next.ms) out[i] = { ...r, ms: next.ms };
  }
  return { rows: out, placements, endsByIndex };
}

export const STRATEGIES = { fixed: fixedStrategy, sequential: sequentialStrategy };
export const DEFAULT_STRATEGY = 'fixed';

// ---------------------------------------------------------------- 本体
/**
 * @param anchors ソースのアンカー `[{ key, ms, index, groupId, ordering, payload }]`
 * @param objects `generate.generateObjects` の結果
 * @param overrides 文書の `anchors`（人が触ったもの）
 * @param strategy 名前か `{ resolve }`
 * @returns `{ rows, byKey, objects }` —— objects には `startMs` / `endMs` / `sequenced` が付く
 */
export function resolveAnchors({ anchors = [], objects = [], overrides = {}, strategy = DEFAULT_STRATEGY, fps = DEFAULT_FPS } = {}) {
  const bySource = new Map();
  for (const o of objects) {
    const key = o?.source?.anchorKey;
    if (!key) continue;
    if (!bySource.has(key)) bySource.set(key, []);
    bySource.get(key).push(o);
  }

  const rows0 = baseRows(anchors, overrides ?? {});
  const run = typeof strategy === 'function' ? strategy : (strategy?.resolve ?? STRATEGIES[strategy] ?? STRATEGIES[DEFAULT_STRATEGY]);
  const { rows, placements = new Map(), endsByIndex = new Map() } = run({ rows: rows0, bySource, fps, objects });

  const byKey = new Map();
  for (const r of rows) if (r.key) byKey.set(r.key, r);

  const placed = objects.map((o) => {
    const hit = placements.get(o.id);
    const openRow = byKey.get(o.source.anchorKey);
    const startMs = hit?.startMs ?? (openRow?.ms ?? 0);
    let endMs;
    if (o.endAnchorKey) {
      const closeRow = byKey.get(o.endAnchorKey);
      endMs = (closeRow?.ms ?? startMs) + (o.endOffsetMs ?? 0);
      // 閉じるアンカーのある常時表示は、その間に起きた帯が終わるまで伸ばす（長さ 0 にしない）
      const from = openRow?.index;
      const to = closeRow?.index;
      if (from != null && to != null) for (let i = from; i <= to; i += 1) endMs = Math.max(endMs, endsByIndex.get(i) ?? -Infinity);
      if (endMs <= startMs) endMs = startMs + (o.minDurationMs ?? 1000);
    } else {
      endMs = hit?.endMs ?? (startMs + durationOf(o));
    }
    return {
      ...o, startMs, endMs: Math.max(startMs, endMs), sequenced: hit?.sequenced ?? false,
      anchorIndex: openRow?.index ?? null,
      hidden: o.hidden === true || openRow?.hidden === true,
    };
  });

  return { rows, byKey, objects: placed };
}
